// ---------------------------------------------------------------------------
// The sync loop (PLAN 5.3).
//
// Runs in the page, not the service worker. Background Sync is Chrome/Edge
// only -- Firefox has taken a negative standards position and iOS implements
// none of it, which is roughly half the volunteer fleet (PLAN 5.1).
//
// Push first, then pull. The push is idempotent by construction: the visit id
// is generated on the device, so a canvasser on flaky signal who fires the same
// batch three times still produces one row (PLAN 5.4).
// ---------------------------------------------------------------------------

import type { Household, Person, TablesInsert, Visit } from '@yucanvas/shared';
import type { Table } from 'dexie';
import { supabase } from '../lib/supabase';
import { chunk } from '../lib/chunk';
import { PAGE_SIZE, fetchAllAfter, keysetAfter } from '../lib/paginate';
import { evictableHouseholds, householdsThatLeft, staleTurfIds } from './reconcile';
import {
  advance,
  freshPosition,
  readPosition,
  serializePosition,
  type PullPosition,
} from './cursor';
import { isDue, isPermanent, retryDelay } from './push-errors';
import {
  CONTACT_COLUMNS,
  HOUSEHOLD_COLUMNS,
  NOTE_COLUMNS,
  PERSON_COLUMNS,
  PUSH_COLUMNS,
  VISIT_COLUMNS,
  toServerRow,
} from './columns';
import {
  EPOCH,
  LAST_SYNC,
  PULL_CURSOR,
  PULL_CURSORS,
  PULL_POSITIONS,
  PULL_SHAPE,
  PULL_SHAPE_VERSION,
  PULL_TABLES,
  PULL_TURFS,
  db,
  getMeta,
  pendingContacts,
  pendingCount,
  pendingHouseholds,
  pendingNotes,
  pendingPeople,
  pendingRemovals,
  pendingRevivals,
  pendingVisits,
  setMeta,
  type LocalHouseNote,
  type LocalPersonContact,
  type LocalVisit,
  type PullTable,
} from './db';

const PUSH_BATCH = 200;

/**
 * Repoints everything on this device from a household id we invented to the
 * one the server already had, then drops ours.
 *
 * Needed when a home turns out to exist server-side under a different id --
 * the address is unique, so the row we queued can never be created, but the
 * visit recorded against it is real and has to survive.
 */
async function adoptServerHousehold(localId: string, serverId: string): Promise<void> {
  await db.transaction(
    'rw',
    [db.households, db.people, db.visits, db.houseNotes],
    async () => {
      await db.people.where('household_id').equals(localId).modify({ household_id: serverId });
      await db.visits.where('household_id').equals(localId).modify({ household_id: serverId });
      await db.houseNotes
        .where('household_id')
        .equals(localId)
        .modify({ household_id: serverId });
      await db.households.delete(localId);
    },
  );
}

/** The server's household at this address, if it already has one. */
async function findServerHousehold(h: {
  turf_id: string | null;
  street_address: string;
  unit: string | null;
  postal_code: string | null;
}): Promise<{ id: string; source: string; active: boolean } | null> {
  if (!h.turf_id) return null;

  let query = supabase
    .from('households')
    .select('id,source,active')
    .eq('turf_id', h.turf_id)
    .eq('street_address', h.street_address);

  query = h.unit === null ? query.is('unit', null) : query.eq('unit', h.unit);
  query = h.postal_code === null
    ? query.is('postal_code', null)
    : query.eq('postal_code', h.postal_code);

  const { data, error } = await query.limit(1).maybeSingle();
  if (error || !data) return null;
  return data;
}

/**
 * Poll cadence. Two, because a fixed 25 seconds was the entire request floor:
 * nine devices idling produced roughly a thousand Data API requests every five
 * minutes against a database holding 61 visits, which is almost all overhead
 * and is what the "error rate is persistently high" advisor was measuring a
 * percentage of.
 *
 * Fast while something is actually happening -- work queued to push, rows
 * coming back from a pull, an app just brought to the foreground -- and slow
 * otherwise. Nothing waits on the slow cadence that a canvasser can see: the
 * push is kicked the moment a visit is recorded, and every page event below
 * kicks too, so a phone coming out of a pocket syncs immediately rather than
 * up to three minutes later.
 */
const ACTIVE_INTERVAL_MS = 25_000;
const IDLE_INTERVAL_MS = 180_000;

/** How long one sign of life keeps the fast cadence. */
const ACTIVE_WINDOW_MS = 5 * 60_000;

let activeUntil = 0;

/** Something happened; stay responsive for a while. */
function markActive(): void {
  activeUntil = Date.now() + ACTIVE_WINDOW_MS;
}

function currentInterval(): number {
  return Date.now() < activeUntil ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS;
}

/**
 * How often evictDeparted() asks the server about every cached home. It reads
 * the whole household store and sends every id, a request per 500, so on
 * every sync it was most of the pull's cost. A home leaves a turf only when an
 * organizer redraws or rescopes it, so the check runs when the app opens, when
 * a pull brings back changed homes (a redraw touches them), and otherwise this
 * often.
 */
const DEPARTED_CHECK_MS = 10 * 60_000;

/** In memory, so every app open checks once. */
let departedCheckedAt = 0;
let departedCheckDue = true;


export interface SyncState {
  pending: number;
  lastSyncAt: string | null;
  lastError: string | null;
  running: boolean;
  online: boolean;
}

let state: SyncState = {
  pending: 0,
  lastSyncAt: null,
  lastError: null,
  running: false,
  online: navigator.onLine,
};

const listeners = new Set<(s: SyncState) => void>();

function emit(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  for (const fn of listeners) fn(state);
}

export function onSyncState(fn: (s: SyncState) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export function syncState(): SyncState {
  return state;
}

/**
 * One table's pull position, its cursor seeded from the single cursor these
 * replaced so a device upgrading mid-shift does not re-pull the whole turf.
 */
async function readPullPosition(table: PullTable, legacy: string | null): Promise<PullPosition> {
  const cursor = (await getMeta(PULL_CURSORS[table])) ?? legacy ?? EPOCH;
  return readPosition(cursor, await getMeta(PULL_POSITIONS[table]));
}

async function refreshPending(): Promise<void> {
  emit({ pending: await pendingCount() });
}

// --- push ------------------------------------------------------------------

/**
 * Everything this device owes the server.
 *
 * Each queue is pushed independently. An earlier version returned early when
 * the visit queue was empty, which stranded the other two: a person contact
 * whose visit had already synced, or a house note written without logging a
 * visit at all, would sit queued forever waiting for a visit that never came.
 */
async function push(retryRejected: boolean): Promise<void> {
  const now = Date.now();
  const due: Due = (row) => isDue(row, now, retryRejected);

  // Order is a foreign key constraint, not a preference. A visit references a
  // household and a contact references a person, so a home added at the door
  // and the resident recorded there must reach the server before the knock
  // that happened at them.
  await pushHouseholds(due);
  await pushPeople(due);
  await pushRemovals();
  await pushRevivals();
  await pushVisits(due);
  await pushContacts(due);
  await pushNotes(due);
}

/** Whether a queued row is sent this time; see retryDelay(). */
type Due = (row: { retry_at?: number }) => boolean;

/** Bookkeeping for a failed push: permanent rejections wait before the next try. */
function failure(attempts: number, error: { message: string; code?: string | null }) {
  return {
    attempts,
    last_error: error.message,
    retry_at: isPermanent(error) ? Date.now() + retryDelay(attempts) : undefined,
  };
}

/**
 * Homes a canvasser added at a door.
 *
 * Through the RPC rather than an insert: households carry no insert policy,
 * and the function is what checks the pin actually falls inside the turf being
 * claimed. The id is ours, so a replayed push is one row.
 */
async function pushHouseholds(due: Due): Promise<void> {
  const queued = (await pendingHouseholds()).filter(due);
  if (queued.length === 0) return;

  for (const h of queued) {
    if (!h.turf_id) {
      // Cannot happen through the UI, which only offers this inside a turf.
      // Reported rather than retried forever if it somehow does.
      await db.households.update(h.id, {
        attempts: (h.attempts ?? 0) + 1,
        last_error: 'This home has no turf and cannot be uploaded.',
      });
      continue;
    }

    // Empty strings rather than nulls: the function maps '' to null itself,
    // and the generated argument types are not nullable.
    const { error } = await supabase.rpc('add_household_at', {
      p_id: h.id,
      p_turf_id: h.turf_id,
      p_lat: h.lat,
      p_lon: h.lon,
      p_street_address: h.street_address,
      p_unit: h.unit ?? '',
      p_city: h.city,
      p_postal_code: h.postal_code ?? '',
    });

    if (error) {
      // The address is unique, so this usually means the home already exists
      // server-side -- often one that was removed and still holds its slot.
      // Adopt it rather than stranding the knock recorded against ours.
      const existing = await findServerHousehold(h);
      if (existing && existing.source === 'manual') {
        if (!existing.active) {
          await supabase.rpc('revive_manual_household', {
            p_id: existing.id,
            p_lat: h.lat,
            p_lon: h.lon,
          });
        }
        await adoptServerHousehold(h.id, existing.id);
        continue;
      }

      await db.households.update(h.id, failure((h.attempts ?? 0) + 1, error));

      // A rejection that will repeat is set aside rather than retried, so the
      // rest of the shift's work is not stuck behind it forever.
      if (isPermanent(error)) continue;
      throw new Error(error.message);
    }

    await db.households.update(h.id, { synced: 1, last_error: null, retry_at: undefined });
  }
}

/**
 * Homes taken back. Runs after pushHouseholds, so a home added and removed
 * while offline is created and then deactivated in the right order rather than
 * the removal failing against a household the server has never seen.
 *
 * (writes.ts deletes such a home outright instead, so this ordering is a
 * safety net rather than the usual path.)
 */
async function pushRemovals(): Promise<void> {
  const queued = await pendingRemovals();
  if (queued.length === 0) return;

  for (const h of queued) {
    const { error } = await supabase.rpc('remove_manual_household', { p_id: h.id });

    if (error) {
      await db.households.update(h.id, {
        attempts: (h.attempts ?? 0) + 1,
        last_error: error.message,
      });
      // A removal the server will always refuse -- someone else's turf, or a
      // home it never had -- is dropped rather than retried forever.
      if (isPermanent(error)) {
        await db.households.update(h.id, { pending_removal: 1 });
        continue;
      }
      throw new Error(error.message);
    }

    await db.households.update(h.id, { pending_removal: 1, last_error: null });
  }
}

/** Homes brought back after being removed, moved to wherever they are now. */
async function pushRevivals(): Promise<void> {
  const queued = await pendingRevivals();
  if (queued.length === 0) return;

  for (const h of queued) {
    const { error } = await supabase.rpc('revive_manual_household', {
      p_id: h.id,
      p_lat: h.lat,
      p_lon: h.lon,
    });

    if (error) {
      await db.households.update(h.id, {
        attempts: (h.attempts ?? 0) + 1,
        last_error: error.message,
      });
      if (isPermanent(error)) {
        await db.households.update(h.id, { pending_revival: 1 });
        continue;
      }
      throw new Error(error.message);
    }

    await db.households.update(h.id, { pending_revival: 1, last_error: null });
  }
}

/** Residents recorded at a door, who are not in the voter file. */
async function pushPeople(due: Due): Promise<void> {
  const queued = (await pendingPeople()).filter(due);
  if (queued.length === 0) return;

  for (const person of queued) {
    const { error } = await supabase.rpc('add_person_manual', {
      p_id: person.id,
      p_household_id: person.household_id,
      p_first_name: person.first_name,
      p_last_name: person.last_name,
    });

    if (error) {
      await db.people.update(person.id, failure((person.attempts ?? 0) + 1, error));
      if (isPermanent(error)) continue;
      throw new Error(error.message);
    }

    await db.people.update(person.id, { synced: 1, last_error: null, retry_at: undefined });
  }
}

/**
 * Pushes queued rows in batches, marking each synced or recording its error.
 *
 * A batch the database will always reject is retried one row at a time, so a
 * single bad row -- a visit at a home that has since left the turf -- is set
 * aside alone instead of taking up to PUSH_BATCH good visits with it. Before
 * this, one canvasser's whole shift sat behind three such homes, 39 visits
 * retried hundreds of times each (27 October 2026).
 *
 * A row rejected on its own then waits out retryDelay() before it is sent
 * again, so it is not re-sent alone on every sync and good rows queued behind
 * it go out in whole batches.
 */
async function pushQueued<T extends { id: string; attempts: number; retry_at?: number }>(
  all: T[],
  table: Table<T, string>,
  due: Due,
  send: (rows: T[]) => PromiseLike<{ error: { message: string; code?: string } | null }>,
): Promise<void> {
  const queued = all.filter(due);

  const pushBatch = async (batch: T[]): Promise<void> => {
    const { error } = await send(batch);

    if (error && isPermanent(error) && batch.length > 1) {
      for (const row of batch) await pushBatch([row]);
      return;
    }

    await db.transaction('rw', table, async () => {
      for (const row of batch) {
        const change = error
          ? failure(row.attempts + 1, error)
          : { synced: 1, last_error: null, retry_at: undefined };
        await table.update(row.id, change as never);
      }
    });

    // A row the database will always reject stays queued but is stepped over,
    // so the pull and mark_synced() still run behind it.
    if (error && !isPermanent(error)) throw new Error(error.message);
  };

  for (let i = 0; i < queued.length; i += PUSH_BATCH) {
    await pushBatch(queued.slice(i, i + PUSH_BATCH));
  }
}

async function pushVisits(due: Due): Promise<void> {
  // ignoreDuplicates maps to ON CONFLICT DO NOTHING on the primary key, so a
  // replayed batch produces one row per visit id, not three (PLAN 13.3).
  await pushQueued(await pendingVisits(), db.visits, due, (batch) =>
    supabase
      .from('visits')
      .upsert(
        batch.map((v) => toServerRow<TablesInsert<'visits'>>(v, PUSH_COLUMNS.visits)),
        { ignoreDuplicates: true },
      ),
  );
}

/** House notes carry no foreign key to a visit, so ordering does not matter. */
async function pushNotes(due: Due): Promise<void> {
  // Columns named rather than spread: author_name and the sync bookkeeping
  // are device-only and have no server column.
  await pushQueued(await pendingNotes(), db.houseNotes, due, (batch) =>
    supabase.from('household_notes').upsert(
      batch.map((n) => ({
        id: n.id,
        household_id: n.household_id,
        author_id: n.author_id,
        body: n.body,
        created_at: n.created_at,
      })),
      { ignoreDuplicates: true },
    ),
  );
}

/**
 * Person contacts push after visits, never before: each one carries a
 * visit_id, and a contact whose visit has not landed violates the foreign key
 * and would be retried forever.
 */
async function pushContacts(due: Due): Promise<void> {
  await pushQueued(await pendingContacts(), db.personContacts, due, (batch) =>
    supabase
      .from('person_contacts')
      .upsert(
        batch.map((c) => toServerRow<TablesInsert<'person_contacts'>>(c, PUSH_COLUMNS.contacts)),
        { ignoreDuplicates: true },
      ),
  );
}

// --- pull ------------------------------------------------------------------

/**
 * Assigned turf that is still being worked. A turf an organizer has marked
 * complete is left out, so evictUnassigned() clears it off the device exactly
 * as it would an unassignment -- and reopening it re-pulls it as a gained turf.
 */
async function myTurfIds(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('turf_assignments')
    .select('turf_id, turfs!inner(completed_at)')
    .eq('canvasser_id', userId)
    .is('turfs.completed_at', null);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => r.turf_id);
}

/**
 * Delta pull. Without it, canvassers with adjacent turf double-knock and a
 * do_not_contact flag set by one volunteer never reaches the others (PLAN 5.5).
 */
/**
 * Removes turf the canvasser is no longer assigned, and everything scoped to
 * it. Runs before the early return below, because losing every assignment is
 * exactly the case that needs cleaning up -- a deleted turf cascades its
 * assignments away, so `turfIds` comes back empty and the pull would otherwise
 * do nothing at all while the app kept showing the turf.
 */
async function evictUnassigned(assigned: readonly string[]): Promise<void> {
  const localTurfs = await db.turfs.toArray();
  const stale = staleTurfIds(localTurfs, assigned);
  if (stale.length === 0) return;

  await db.transaction(
    'rw',
    [db.turfs, db.households, db.people, db.visits, db.personContacts],
    async () => {
      const staleSet = new Set(stale);
      const households = await db.households.where('turf_id').anyOf(stale).toArray();

      // Households still holding work this device has not pushed are kept.
      const queued = await db.visits.where('synced').equals(0).toArray();
      const holding = new Set(queued.map((v) => v.household_id));

      const evict = evictableHouseholds(households, staleSet, holding);
      await dropHouseholds(evict);
      await db.turfs.bulkDelete(stale);
    },
  );
}

/**
 * Deletes households from the device with everything scoped to them, keeping
 * only unsent work. Called inside a transaction over the tables it touches.
 */
async function dropHouseholds(ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  const evictSet = new Set(ids);

  const people = await db.people.where('household_id').anyOf(ids).toArray();
  const personIds = people.filter((p) => evictSet.has(p.household_id)).map((p) => p.id);

  // Only rows the server already has. A queued contact is unsent work.
  const contacts = await db.personContacts.where('person_id').anyOf(personIds).toArray();
  await db.personContacts.bulkDelete(contacts.filter((c) => c.synced === 1).map((c) => c.id));

  const visits = await db.visits.where('household_id').anyOf(ids).toArray();
  await db.visits.bulkDelete(visits.filter((v) => v.synced === 1).map((v) => v.id));

  await db.people.bulkDelete(personIds);
  await db.households.bulkDelete([...ids]);
}

/** Ids per household_turfs() call, safely under the server's 1,000-row cap. */
const HOUSEHOLD_TURFS_BATCH = 500;

/**
 * Removes homes that left the canvasser's turf while the turf stayed
 * assigned. The delta pull filters on turf_id, so the update that moved a home
 * out never matches it and the device would show the door forever -- and
 * reject every visit logged there (see 20260928000100_household_turfs.sql).
 */
async function evictDeparted(assigned: readonly string[]): Promise<void> {
  const local = await db.households.toArray();
  const asked = local
    .filter((h) => h.synced !== 0 && h.pending_removal !== 0 && h.pending_revival !== 0)
    .map((h) => h.id);
  if (asked.length === 0) return;

  // Asked in pieces: the server caps every response at 1,000 rows, and a home
  // missing from the answer is read as one that left. One call for a device
  // holding more than that evicted everything past the cap.
  const current = new Map<string, string | null>();
  for (const ids of chunk(asked, HOUSEHOLD_TURFS_BATCH)) {
    const { data, error } = await supabase.rpc('household_turfs', { p_ids: ids });
    if (error) throw new Error(error.message);
    for (const r of data ?? []) current.set(r.id, r.turf_id);
  }

  await db.transaction(
    'rw',
    [db.households, db.people, db.visits, db.personContacts],
    async () => {
      const queued = await db.visits.where('synced').equals(0).toArray();
      const holding = new Set(queued.map((v) => v.household_id));
      await dropHouseholds(householdsThatLeft(asked, current, assigned, holding));
    },
  );
}

async function pull(userId: string): Promise<void> {
  const turfIds = await myTurfIds(userId);
  await evictUnassigned(turfIds);
  if (departedCheckDue || Date.now() - departedCheckedAt >= DEPARTED_CHECK_MS) {
    await evictDeparted(turfIds);
    departedCheckedAt = Date.now();
    departedCheckDue = false;
  }
  if (turfIds.length === 0) return;

  // A cursor only means "everything before this was collected". When the pull
  // changes what it collects, that stops being true, so the cursors are
  // discarded and the turf is re-pulled once.
  const shape = await getMeta(PULL_SHAPE);
  const shapeChanged = shape !== PULL_SHAPE_VERSION;

  // Gaining a turf has to re-pull, and the cursor is why. Households are
  // filtered on `updated_at > cursor` while turf is delivered unfiltered, so a
  // canvasser assigned to a turf that was populated days ago -- which is the
  // normal case, since turf is drawn and populated long before anyone is put
  // on it -- received the turf row and not one household. The screen then read
  // "0 of 0 homes" with nothing to download, and no amount of syncing fixed
  // it, because every later pull asked the same question and got the same
  // empty answer.
  //
  // Losing one needs no re-pull: evictUnassigned() above has already dropped
  // it, and nothing that remains is newer than the cursor.
  const turfKey = [...turfIds].sort().join(',');
  const seenTurfs = (await getMeta(PULL_TURFS))?.split(',').filter(Boolean) ?? [];
  const gainedTurf = turfIds.some((id) => !seenTurfs.includes(id));

  // Otherwise seeded from the cursor these replaced, so upgrading mid-shift
  // does not re-pull the whole turf over a phone connection.
  const refetchAll = shapeChanged || gainedTurf;
  const legacy = refetchAll ? EPOCH : await getMeta(PULL_CURSOR);
  const positions = Object.fromEntries(
    await Promise.all(
      PULL_TABLES.map(async (table) => [
        table,
        refetchAll ? freshPosition(EPOCH) : await readPullPosition(table, legacy),
      ]),
    ),
  ) as Record<PullTable, PullPosition>;

  const { data: turfs, error: turfError } = await supabase
    .from('turfs')
    .select('*')
    .in('id', turfIds);
  if (turfError) throw new Error(turfError.message);

  // When each table's query started and finished on the device clock, which
  // is what advance() needs to know whether a timestamp has settled.
  const timings = {} as Record<PullTable, { startedAt: number; finishedAt: number }>;
  const timed = async <T>(table: PullTable, run: () => Promise<T>): Promise<T> => {
    const startedAt = Date.now();
    const result = await run();
    timings[table] = { startedAt, finishedAt: Date.now() };
    return result;
  };

  // Every query below pages by (stamp, id) rather than by offset -- see
  // fetchAllAfter() -- so each orders by both and limits to PAGE_SIZE.
  const households = (await timed('households', () =>
    fetchAllAfter('updated_at', (after) => {
      let query = supabase
        .from('households')
        .select(HOUSEHOLD_COLUMNS)
        .in('turf_id', turfIds)
        .gt('updated_at', positions.households.cursor);
      if (after) query = query.or(keysetAfter('updated_at', after));
      return query.order('updated_at').order('id').limit(PAGE_SIZE);
    }),
  )) as unknown as Household[];

  // Scoped through an inner join on the household's turf rather than a list of
  // household ids: a turf has thousands of them and the id list would not fit
  // in a URL.
  const visitRows = (await timed('visits', () =>
    fetchAllAfter('received_at', (after) => {
      let query = supabase
        .from('visits')
        .select(`${VISIT_COLUMNS},households!inner(turf_id)`)
        .in('households.turf_id', turfIds)
        .gt('received_at', positions.visits.cursor);
      if (after) query = query.or(keysetAfter('received_at', after));
      return query.order('received_at').order('id').limit(PAGE_SIZE);
    }),
  )) as unknown as (Visit & { households?: unknown })[];

  // The voter file for this turf. Cached on the device like households --
  // see docs/campaign-requirements.md § 5.1, which is still open.
  //
  // Scoped through the household's turf, not through the households returned
  // above. Those are only the households that changed in THIS pull, so in
  // steady state the list was empty and no person was ever refetched -- which
  // meant set_person_status() marking someone deceased or moved reached
  // nobody else's device, because it does not touch the household row.
  const people = (await timed('people', () =>
    fetchAllAfter('updated_at', (after) => {
      let query = supabase
        .from('people')
        .select(`${PERSON_COLUMNS},households!inner(turf_id)`)
        .in('households.turf_id', turfIds)
        .gt('updated_at', positions.people.cursor);
      if (after) query = query.or(keysetAfter('updated_at', after));
      return query.order('updated_at').order('id').limit(PAGE_SIZE);
    }),
  )) as unknown as (Person & { households?: unknown })[];

  // Scoped through the visit rather than a person id list, for the same reason
  // the visit pull joins on turf: the id list would not fit in a URL.
  const contactRows = (await timed('contacts', () =>
    fetchAllAfter('received_at', (after) => {
      let query = supabase
        .from('person_contacts')
        .select(`${CONTACT_COLUMNS},visits!inner(household_id,households!inner(turf_id))`)
        .in('visits.households.turf_id', turfIds)
        .gt('received_at', positions.contacts.cursor);
      if (after) query = query.or(keysetAfter('received_at', after));
      return query.order('received_at').order('id').limit(PAGE_SIZE);
    }),
  )) as unknown as (Record<string, unknown> & { received_at: string })[];

  // Notes about an address, scoped through the household's turf like the rest.
  // The author's name is resolved here so the sheet can attribute a note
  // without the device holding a copy of the roster.
  const noteRows = (await timed('notes', () =>
    fetchAllAfter('received_at', (after) => {
      let query = supabase
        .from('household_notes')
        .select(`${NOTE_COLUMNS},households!inner(turf_id),profiles(name)`)
        .in('households.turf_id', turfIds)
        .gt('received_at', positions.notes.cursor);
      if (after) query = query.or(keysetAfter('received_at', after));
      return query.order('received_at').order('id').limit(PAGE_SIZE);
    }),
  )) as unknown as (Record<string, unknown> & { received_at: string })[];

  // Every query succeeded; only now is it safe to write and advance
  // (PLAN 5.3). Each cursor moves on its own table's rows, so a large visit
  // batch cannot drag the household cursor past an update it never fetched.

  await db.transaction(
    'rw',
    [db.turfs, db.households, db.visits, db.people, db.personContacts, db.houseNotes],
    async () => {
    if (turfs && turfs.length > 0) await db.turfs.bulkPut(turfs);
    if (households.length > 0) {
      // bulkPut would drop the pending flag off a home added at a door that
      // the server has echoed back, and re-adding it would then fail. Rows
      // still queued keep their local copy until they have pushed.
      const queuedIds = new Set([
        ...((await db.households.where('synced').equals(0).primaryKeys()) as string[]),
        // A home whose removal has not reached the server yet would otherwise
        // come back active on the next pull and look undeleted.
        ...((await db.households
          .where('pending_removal')
          .equals(0)
          .primaryKeys()) as string[]),
        ...((await db.households
          .where('pending_revival')
          .equals(0)
          .primaryKeys()) as string[]),
      ]);
      const safe = households.filter((h) => !queuedIds.has(h.id));
      if (safe.length > 0) await db.households.bulkPut(safe);
    }

    for (const row of visitRows) {
      const { households: _join, ...visit } = row;
      void _join;
      // A visit that came back from the server is by definition synced,
      // including one this device queued moments ago.
      await db.visits.put({
        ...(visit as Visit),
        synced: 1,
        attempts: 0,
        last_error: null,
      });
    }

    if (people.length > 0) {
      const queuedPeople = new Set(
        (await db.people.where('synced').equals(0).primaryKeys()) as string[],
      );
      await db.people.bulkPut(
        people
          .filter((p) => !queuedPeople.has(p.id))
          .map(({ households: _join, ...person }) => person as Person),
      );
    }

    for (const row of noteRows) {
      const {
        households: _turfJoin,
        profiles: author,
        ...note
      } = row as Record<string, unknown> & { profiles?: { name?: string } | null };
      void _turfJoin;
      await db.houseNotes.put({
        ...(note as unknown as LocalHouseNote),
        author_name: author?.name ?? null,
        synced: 1,
        attempts: 0,
        last_error: null,
      });
    }

    for (const row of contactRows) {
      const { visits: _visitJoin, ...contact } = row as Record<string, unknown>;
      void _visitJoin;
      await db.personContacts.put({
        ...(contact as unknown as LocalPersonContact),
        synced: 1,
        attempts: 0,
        last_error: null,
      });
    }
  });

  // Recorded only after a pull has succeeded end to end, so an interrupted
  // re-pull is retried rather than marked done.
  if (shapeChanged) await setMeta(PULL_SHAPE, PULL_SHAPE_VERSION);
  if (turfKey !== (await getMeta(PULL_TURFS))) await setMeta(PULL_TURFS, turfKey);

  const stamps: Record<PullTable, string[]> = {
    households: households.map((h) => h.updated_at),
    visits: visitRows.map((v) => v.received_at),
    people: people.map((p) => p.updated_at),
    contacts: contactRows.map((c) => c.received_at),
    notes: noteRows.map((n) => n.received_at),
  };

  let fresh = 0;
  const writes: Promise<void>[] = [];
  for (const table of PULL_TABLES) {
    const { startedAt, finishedAt } = timings[table];
    const next = advance(positions[table], stamps[table], startedAt, finishedAt);
    fresh += next.fresh;
    // A redraw touches every home it moves, so changed homes are the cue to
    // check for ones that moved out (see DEPARTED_CHECK_MS).
    if (table === 'households' && next.fresh > 0) departedCheckDue = true;
    writes.push(
      setMeta(PULL_CURSORS[table], next.position.cursor),
      setMeta(PULL_POSITIONS[table], serializePosition(next.position)),
    );
  }
  await Promise.all(writes);

  // Rows newer than anything seen before mean somebody else is working, so
  // stay on the fast cadence rather than letting a shift drift three minutes
  // behind. Rows re-read inside the overlap do not count: they kept every
  // device on the fast cadence forever.
  if (fresh > 0) markActive();
}

// --- the loop --------------------------------------------------------------

let inFlight: Promise<void> | null = null;

/**
 * `retryRejected` also sends rows the server refused outright that are still
 * waiting out their retry delay. It is the "Sync now" button: somebody asking
 * by hand, usually because an organizer has just fixed what was refused.
 */
export function syncNow(options: { retryRejected?: boolean } = {}): Promise<void> {
  if (inFlight) {
    // The sync already running may have skipped those rows, so go again after.
    return options.retryRejected ? inFlight.then(() => syncNow(options)) : inFlight;
  }

  inFlight = (async () => {
    // Everything is inside the try, including the session lookup. An early
    // return here would skip the finally below and leave inFlight holding a
    // settled promise, and since every later call short-circuits on it, one
    // kick without a session would silently stop this device syncing for the
    // rest of the session -- a shift's knocks sitting in IndexedDB with the
    // badge insisting they are on their way.
    try {
      const { data } = await supabase.auth.getSession();
      const userId = data.session?.user.id;
      if (!userId) return;

      emit({ running: true, online: navigator.onLine });
      await push(options.retryRejected ?? false);
      await pull(userId);

      // Recorded here, not inside push(), and only once a full sync has
      // succeeded. It used to run only when visits had been pushed, so
      // last_sync_at meant "last logged a knock" rather than "last reached the
      // server" -- and the organizer's volunteer table, whose whole job is
      // showing who is out of contact, reported the wrong thing (PLAN 5.7).
      await supabase.rpc('mark_synced');

      const now = new Date().toISOString();
      await setMeta(LAST_SYNC, now);
      emit({ lastSyncAt: now, lastError: null });
    } catch (cause) {
      emit({ lastError: cause instanceof Error ? cause.message : 'Sync failed' });
    } finally {
      await refreshPending();
      emit({ running: false });
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Triggers, per PLAN 5.3. All of them are page events: nothing here depends on
 * the service worker being alive.
 */
export function startSyncLoop(): () => void {
  const kick = () => void syncNow();

  const onOnline = () => {
    emit({ online: true });
    markActive();
    kick();
  };
  const onOffline = () => emit({ online: false });
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    markActive();
    kick();
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);

  // setTimeout rather than setInterval: the delay is decided fresh each round,
  // so the loop can slow down and speed up without being torn down.
  let timer = 0;
  const schedule = () => {
    timer = window.setTimeout(() => {
      kick();
      schedule();
    }, currentInterval());
  };

  void getMeta(LAST_SYNC).then((v) => emit({ lastSyncAt: v }));
  void refreshPending();
  // Opening the app is a sign of life, so the first minutes are always fast.
  markActive();
  kick();
  schedule();

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearTimeout(timer);
  };
}

/** Called by writes.ts the moment a visit lands in the local store. */
export function notifyQueued(): void {
  // A knock just landed in the local store, so this device is mid-shift:
  // hold the fast cadence whether or not the push can go out right now.
  markActive();
  void refreshPending();
  if (navigator.onLine) void syncNow();
}
